import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Panel, PanelHeader, PanelHeaderBack, Group, Div, Cell, Button, Spinner,
  FormItem, Input, Text, Headline, ModalRoot,
  ModalPageHeader, IconButton, Checkbox, Snackbar, Card, Select,
} from '@vkontakte/vkui';
import {
  Icon24Add, Icon24Delete, Icon24Write, Icon24Cancel,
} from '@vkontakte/icons';
import { useWeddingStore } from '../../stores/wedding';
import { api } from '../../api/client';
import type { WeddingEvent, Guest, RSVPStatus } from '../../types';
import { DragHandle } from '../../ui/DragHandle';
import { ResizableModalPage, sheetId } from '../../ui/ResizableModalPage';

const EVENT_ICONS = ['💍', '🎉', '🥂', '💃', '🕺', '🎵', '📸', '🍽', '✈️', '🎂', '💐'];
const RSVP_LABEL: Record<RSVPStatus, string> = {
  accepted: 'Идёт',
  pending: 'Ожидание',
  denied: 'Не идёт',
};

type FormState = {
  name: string;
  note: string;
  icon_id: number;
  date_start: string;
  date_end: string;
  address: string;
};

const emptyForm: FormState = {
  name: '',
  note: '',
  icon_id: 0,
  date_start: '',
  date_end: '',
  address: '',
};

type AttendanceItem = {
  id: string;
  guest_id: string;
  rsvp_status: RSVPStatus;
  invitation_sent: boolean;
  guest_first_name?: string | null;
  guest_last_name?: string | null;
};

type AttendanceList = {
  items: AttendanceItem[];
  accepted: number;
  pending: number;
  denied: number;
};

function toLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function EventsPage() {
  const navigate = useNavigate();
  const {
    currentWedding, weddingsLoaded, events, guests, loadEvents, loadGuests,
  } = useWeddingStore();

  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<'create' | 'edit' | 'rsvp' | 'add-guests' | null>(null);
  const [editing, setEditing] = useState<WeddingEvent | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);
  const [openToken, setOpenToken] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState<WeddingEvent | null>(null);

  const [rsvpEvent, setRsvpEvent] = useState<WeddingEvent | null>(null);
  const [attendance, setAttendance] = useState<AttendanceList | null>(null);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [selectedGuests, setSelectedGuests] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!currentWedding) {
      if (weddingsLoaded) navigate('/');
      return;
    }
    setLoading(true);
    Promise.all([loadEvents(), loadGuests()]).finally(() => setLoading(false));
  }, [currentWedding, weddingsLoaded, loadEvents, loadGuests, navigate]);

  const sorted = useMemo(() => {
    return [...events].sort((a, b) => {
      const ta = a.date_start ? new Date(a.date_start).getTime() : Number.MAX_SAFE_INTEGER;
      const tb = b.date_start ? new Date(b.date_start).getTime() : Number.MAX_SAFE_INTEGER;
      return ta - tb || a.position - b.position;
    });
  }, [events]);

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setOpenToken((t) => t + 1);
    setModal('create');
  }

  function openEdit(e: WeddingEvent) {
    setEditing(e);
    setForm({
      name: e.name,
      note: e.note || '',
      icon_id: e.icon_id ?? 0,
      date_start: toLocalInput(e.date_start),
      date_end: toLocalInput(e.date_end),
      address: e.address || '',
    });
    setOpenToken((t) => t + 1);
    setModal('edit');
  }

  async function handleSave() {
    if (!currentWedding || !form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        note: form.note.trim() || null,
        icon_id: form.icon_id,
        date_start: form.date_start ? new Date(form.date_start).toISOString() : null,
        date_end: form.date_end ? new Date(form.date_end).toISOString() : null,
        address: form.address.trim() || null,
      };
      if (editing) {
        await api.patch(`weddings/${currentWedding.id}/events/${editing.id}`, payload);
        setSnack('Мероприятие обновлено');
      } else {
        await api.post(`weddings/${currentWedding.id}/events`, payload);
        setSnack('Мероприятие создано');
      }
      await loadEvents();
      setOpenToken((t) => t + 1);
      setModal(null);
    } catch {
      setSnack('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(e: WeddingEvent) {
    if (!currentWedding) return;
    try {
      await api.delete(`weddings/${currentWedding.id}/events/${e.id}`);
      await loadEvents();
      setSnack('Мероприятие удалено');
    } catch {
      setSnack('Ошибка удаления');
    } finally {
      setOpenToken((t) => t + 1);
      setConfirmDelete(null);
    }
  }

  async function openRsvp(e: WeddingEvent) {
    if (!currentWedding) return;
    setRsvpEvent(e);
    setOpenToken((t) => t + 1);
    setModal('rsvp');
    setRsvpLoading(true);
    try {
      const res = await api.get<AttendanceList>(`weddings/${currentWedding.id}/events/${e.id}/attendance`);
      setAttendance(res);
    } catch {
      setSnack('Ошибка загрузки гостей');
    } finally {
      setRsvpLoading(false);
    }
  }

  async function setRsvp(attendanceId: string, status: RSVPStatus) {
    if (!currentWedding || !rsvpEvent) return;
    try {
      await api.patch(
        `weddings/${currentWedding.id}/events/${rsvpEvent.id}/attendance/${attendanceId}`,
        { rsvp_status: status },
      );
      const res = await api.get<AttendanceList>(
        `weddings/${currentWedding.id}/events/${rsvpEvent.id}/attendance`,
      );
      setAttendance(res);
    } catch {
      setSnack('Ошибка обновления RSVP');
    }
  }

  async function removeAttendance(attendanceId: string) {
    if (!currentWedding || !rsvpEvent) return;
    try {
      await api.delete(
        `weddings/${currentWedding.id}/events/${rsvpEvent.id}/attendance/${attendanceId}`,
      );
      const res = await api.get<AttendanceList>(
        `weddings/${currentWedding.id}/events/${rsvpEvent.id}/attendance`,
      );
      setAttendance(res);
      setSnack('Гость убран с мероприятия');
    } catch {
      setSnack('Ошибка удаления');
    }
  }

  function openAddGuests() {
    const already = new Set((attendance?.items || []).map((i) => i.guest_id));
    setSelectedGuests(new Set(guests.filter((g) => !already.has(g.id)).map((g) => g.id)));
    setOpenToken((t) => t + 1);
    setModal('add-guests');
  }

  async function handleAddGuests() {
    if (!currentWedding || !rsvpEvent || selectedGuests.size === 0) return;
    setSaving(true);
    try {
      await api.post(
        `weddings/${currentWedding.id}/events/${rsvpEvent.id}/attendance`,
        { guest_ids: [...selectedGuests], rsvp_status: 'pending' },
      );
      const res = await api.get<AttendanceList>(
        `weddings/${currentWedding.id}/events/${rsvpEvent.id}/attendance`,
      );
      setAttendance(res);
      setOpenToken((t) => t + 1);
      setModal('rsvp');
      setSnack(`Добавлено: ${selectedGuests.size}`);
    } catch {
      setSnack('Ошибка добавления');
    } finally {
      setSaving(false);
    }
  }

  const availableGuests: Guest[] = useMemo(() => guests, [guests]);

  return (
    <Panel>
      <PanelHeader before={<PanelHeaderBack onClick={() => navigate('/')} />}>
        Мероприятия
      </PanelHeader>

      <Group>
        <Div>
          <Button size="l" mode="primary" stretched before={<Icon24Add />} onClick={openCreate}>
            Новое мероприятие
          </Button>
        </Div>
      </Group>

      <Group header={<Text>Всего: {sorted.length}</Text>}>
        {loading ? (
          <Div style={{ textAlign: 'center', padding: 40 }}><Spinner size="l" /></Div>
        ) : sorted.length === 0 ? (
          <Div style={{ textAlign: 'center', padding: 32 }}>
            <Headline level="2">Пока нет мероприятий</Headline>
            <Text style={{ marginTop: 8, color: 'var(--vkui--color_text_secondary)' }}>
              Создайте церемонию, банкет или вечеринку
            </Text>
          </Div>
        ) : (
          sorted.map((e) => (
            <Card key={e.id} style={{ margin: '8px 12px', padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ fontSize: 28, lineHeight: 1 }}>{EVENT_ICONS[e.icon_id ?? 0] || '💍'}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Headline level="2">{e.name}</Headline>
                  {e.date_start && (
                    <Text style={{ color: 'var(--vkui--color_text_secondary)', marginTop: 4 }}>
                      {formatDate(e.date_start)}
                      {e.date_end ? ` — ${formatDate(e.date_end)}` : ''}
                    </Text>
                  )}
                  {e.address && (
                    <Text style={{ marginTop: 2, color: 'var(--vkui--color_text_secondary)' }}>
                      📍 {e.address}
                    </Text>
                  )}
                  {e.note && (
                    <Text style={{ marginTop: 4 }}>{e.note}</Text>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <IconButton label="Изменить" onClick={() => openEdit(e)}>
                    <Icon24Write />
                  </IconButton>
                  <IconButton label="Удалить" onClick={() => setConfirmDelete(e)}>
                    <Icon24Delete />
                  </IconButton>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <Button
                  size="s"
                  mode="secondary"
                  style={{ flex: 1, minWidth: 120 }}
                  onClick={() => openRsvp(e)}
                >
                  Гости и RSVP
                </Button>
                <Button
                  size="s"
                  mode="secondary"
                  style={{ flex: 1, minWidth: 120 }}
                  onClick={() => navigate(`/schedule?event=${e.id}`)}
                >
                  Расписание
                </Button>
              </div>
            </Card>
          ))
        )}
      </Group>

      <ModalRoot
        activeModal={
          modal === 'rsvp' ? sheetId('event-rsvp', openToken)
            : modal === 'add-guests' ? sheetId('event-add-guests', openToken)
              : modal === 'create' || modal === 'edit' ? sheetId('event-form', openToken) : null
        }
        onClose={() => setModal(null)}
      >
        <ResizableModalPage openToken={openToken} id="event-form" onClose={() => setModal(null)} settlingHeight={80}>
          <ModalPageHeader>
            {editing ? 'Редактировать' : 'Новое мероприятие'}
          </ModalPageHeader>
        <DragHandle />
          <Group>
            <FormItem top="Название *">
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Церемония"
              />
            </FormItem>
            <FormItem top="Иконка">
              <Select
                value={String(form.icon_id)}
                onChange={(e) => setForm({ ...form, icon_id: Number(e.target.value) })}
                options={EVENT_ICONS.map((icon, i) => ({ label: `${icon} #${i}`, value: String(i) }))}
              />
            </FormItem>
            <FormItem top="Начало">
              <Input
                type="datetime-local"
                value={form.date_start}
                onChange={(e) => setForm({ ...form, date_start: e.target.value })}
              />
            </FormItem>
            <FormItem top="Окончание">
              <Input
                type="datetime-local"
                value={form.date_end}
                onChange={(e) => setForm({ ...form, date_end: e.target.value })}
              />
            </FormItem>
            <FormItem top="Адрес">
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Ресторан «Ромашка»"
              />
            </FormItem>
            <FormItem top="Заметка">
              <Input
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
              />
            </FormItem>
            <Div>
              <Button size="l" mode="primary" stretched onClick={handleSave} disabled={saving || !form.name.trim()}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </Button>
              <Button size="l" mode="secondary" stretched style={{ marginTop: 8 }} onClick={() => setModal(null)}>
                Отмена
              </Button>
            </Div>
          </Group>
        </ResizableModalPage>

        <ResizableModalPage openToken={openToken} id="event-rsvp" onClose={() => setModal(null)} settlingHeight={80}>
          <ModalPageHeader
            after={
              <Button size="s" mode="secondary" onClick={openAddGuests}>
                + Гости
              </Button>
            }
          >
            {rsvpEvent ? `RSVP: ${rsvpEvent.name}` : 'RSVP'}
          </ModalPageHeader>
        <DragHandle />
          <Group>
            {rsvpLoading ? (
              <Div style={{ textAlign: 'center', padding: 32 }}><Spinner /></Div>
            ) : attendance ? (
              <>
                <Div style={{ display: 'flex', gap: 12 }}>
                  <Card style={{ flex: 1, padding: 10, textAlign: 'center' }}>
                    <Headline level="2">{attendance.accepted}</Headline>
                    <Text>Идут</Text>
                  </Card>
                  <Card style={{ flex: 1, padding: 10, textAlign: 'center' }}>
                    <Headline level="2">{attendance.pending}</Headline>
                    <Text>Ожидают</Text>
                  </Card>
                  <Card style={{ flex: 1, padding: 10, textAlign: 'center' }}>
                    <Headline level="2">{attendance.denied}</Headline>
                    <Text>Не идут</Text>
                  </Card>
                </Div>
                {attendance.items.length === 0 ? (
                  <Div>
                    <Text style={{ color: 'var(--vkui--color_text_secondary)' }}>
                      Гости ещё не привязаны
                    </Text>
                  </Div>
                ) : (
                  attendance.items.map((item) => (
                    <Cell
                      key={item.id}
                      subtitle={RSVP_LABEL[item.rsvp_status]}
                      after={
                        <>
                          <Select
                            style={{ width: 110 }}
                            value={item.rsvp_status}
                            onChange={(e) => setRsvp(item.id, e.target.value as RSVPStatus)}
                            options={[
                              { label: 'Ожидание', value: 'pending' },
                              { label: 'Идёт', value: 'accepted' },
                              { label: 'Не идёт', value: 'denied' },
                            ]}
                          />
                          <IconButton label="Убрать" onClick={() => removeAttendance(item.id)}>
                            <Icon24Delete />
                          </IconButton>
                        </>
                      }
                    >
                      {item.guest_first_name}{item.guest_last_name ? ` ${item.guest_last_name}` : ''}
                    </Cell>
                  ))
                )}
              </>
            ) : (
              <Div>
                <Text>Не удалось загрузить</Text>
              </Div>
            )}
          </Group>
        </ResizableModalPage>

        <ResizableModalPage openToken={openToken} id="event-add-guests" onClose={() => setModal('rsvp')} settlingHeight={80}>
          <ModalPageHeader>Добавить гостей</ModalPageHeader>
        <DragHandle />
          <Group>
            {availableGuests.length === 0 ? (
              <Div>
                <Text>Сначала добавьте гостей в модуле «Гости»</Text>
              </Div>
            ) : (
              availableGuests.map((g) => (
                <Checkbox
                  key={g.id}
                  checked={selectedGuests.has(g.id)}
                  onChange={(e) => {
                    const next = new Set(selectedGuests);
                    if (e.target.checked) next.add(g.id);
                    else next.delete(g.id);
                    setSelectedGuests(next);
                  }}
                >
                  {g.first_name}{g.last_name ? ` ${g.last_name}` : ''}
                </Checkbox>
              ))
            )}
            <Div>
              <Button
                size="l"
                mode="primary"
                stretched
                onClick={handleAddGuests}
                disabled={saving || selectedGuests.size === 0}
              >
                {saving ? 'Добавление…' : `Добавить (${selectedGuests.size})`}
              </Button>
              <Button size="l" mode="secondary" stretched style={{ marginTop: 8 }} onClick={() => setModal('rsvp')}>
                Назад
              </Button>
            </Div>
          </Group>
        </ResizableModalPage>
      </ModalRoot>

      {confirmDelete && (
        <Div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Card style={{ padding: 20, width: 280 }}>
            <Headline level="2">Удалить «{confirmDelete.name}»?</Headline>
            <Button
              mode="primary"
              stretched
              style={{ marginTop: 16 }}
              onClick={() => handleDelete(confirmDelete)}
            >
              Удалить
            </Button>
            <Button mode="secondary" stretched style={{ marginTop: 8 }} onClick={() => setConfirmDelete(null)}>
              Отмена
            </Button>
          </Card>
        </Div>
      )}

      {snack && (
        <Snackbar
          onClose={() => setSnack(null)}
          onClosed={() => setSnack(null)}
          before={<Icon24Cancel />}
        >
          {snack}
        </Snackbar>
      )}

      <Div style={{ height: 24 }} />
    </Panel>
  );
}
